// Redacts payment-card and credentials data from values destined for logs.
// PCI-DSS forbids storing full PAN in logs even when masked at display time;
// terminal SDKs hand back receipt blobs that include partial PAN, auth codes,
// and integration keys, which then get JSON.stringify'd into console output.
// Pass anything you'd otherwise log through `redactSensitive` first.

const SENSITIVE_KEYS = new Set([
  'pan',
  'cardpan',
  'cardnumber',
  'accountnumber',
  'elidedpan',
  'integrationkey',
  'apikey',
  'secret',
  'secretkey',
  'token',
  'authorization',
  'authcode',
  'approvalcode',
  'authorisationcode',
  'authorizationcode',
  'track1',
  'track2',
  'dukpt',
  'ksn',
  'cvv',
  'cvc',
  'cvn',
  'pin',
  'password',
  'pwd',
])

const RECEIPT_KEYS = new Set([
  'merchantreceipt',
  'customerreceipt',
  'receipt',
  'signature',
  'signatureimage',
])

const REDACTED = '[REDACTED]'
const MAX_DEPTH = 6
const MAX_STRING_LEN = 512

function redactString(s) {
  if (s.length > MAX_STRING_LEN) {
    return `${s.slice(0, MAX_STRING_LEN)}…[+${s.length - MAX_STRING_LEN} chars]`
  }
  return s
}

export function redactSensitive(value, depth = 0) {
  if (value == null) return value
  if (depth > MAX_DEPTH) return '[depth-limit]'

  const t = typeof value
  if (t === 'string') return redactString(value)
  if (t !== 'object') return value

  if (Array.isArray(value)) {
    return value.map((v) => redactSensitive(v, depth + 1))
  }

  const out = {}
  for (const [k, v] of Object.entries(value)) {
    const key = String(k).toLowerCase().replace(/[_\-\s]/g, '')
    if (SENSITIVE_KEYS.has(key)) {
      out[k] = REDACTED
    } else if (RECEIPT_KEYS.has(key)) {
      // Receipt blobs are large and may include partial PAN. Replace with a
      // length marker so logs still tell us the receipt was present.
      if (typeof v === 'string') {
        out[k] = `[receipt:${v.length}c]`
      } else if (v != null) {
        out[k] = `[receipt:object]`
      } else {
        out[k] = v
      }
    } else {
      out[k] = redactSensitive(v, depth + 1)
    }
  }
  return out
}

// Convenience: pass through stringification with redaction applied first.
// Keeps console output structured (object) but with sensitive fields gone.
export function safeStringify(value) {
  try {
    return JSON.stringify(redactSensitive(value), null, 2)
  } catch {
    return '[unstringifiable]'
  }
}
